import { fetchAuthSession } from 'aws-amplify/auth'
import { formatDateTime } from './dates'
import type { AccelMode } from './persist'
import {
  S3Client,
  ListObjectsV2Command,
  PutObjectCommand,
  GetObjectCommand,
  DeleteObjectCommand,
} from '@aws-sdk/client-s3'
import { AWS_REGION, USER_DATA_BUCKET } from '../amplify'

export interface StoredSession {
  key: string
  name: string
  size: number
  lastModified: Date
}

/** S3 client whose credentials come from the Cognito identity pool for the signed-in user. */
async function clientAndPrefix(): Promise<{ s3: S3Client; prefix: string }> {
  const session = await fetchAuthSession()
  if (!session.credentials || !session.identityId) {
    throw new Error('Not signed in')
  }
  const s3 = new S3Client({ region: AWS_REGION, credentials: session.credentials })
  return { s3, prefix: `private/${session.identityId}/` }
}

export async function listSessions(): Promise<StoredSession[]> {
  const { s3, prefix } = await clientAndPrefix()
  const out: StoredSession[] = []
  let token: string | undefined
  do {
    const res = await s3.send(
      new ListObjectsV2Command({ Bucket: USER_DATA_BUCKET, Prefix: prefix, ContinuationToken: token }),
    )
    for (const o of res.Contents ?? []) {
      if (!o.Key) continue
      const name = o.Key.slice(prefix.length)
      if (name.startsWith('_')) continue // app bookkeeping files such as _notes.json
      out.push({
        key: o.Key,
        name,
        size: o.Size ?? 0,
        lastModified: o.LastModified ?? new Date(0),
      })
    }
    token = res.IsTruncated ? res.NextContinuationToken : undefined
  } while (token)
  return out.sort((a, b) => b.lastModified.getTime() - a.lastModified.getTime())
}

export async function uploadSession(file: File): Promise<string> {
  const { s3, prefix } = await clientAndPrefix()
  const key = `${prefix}${file.name}`
  await s3.send(
    new PutObjectCommand({
      Bucket: USER_DATA_BUCKET,
      Key: key,
      Body: new Uint8Array(await file.arrayBuffer()),
      ContentType: 'application/octet-stream',
    }),
  )
  return key
}

export async function downloadSession(key: string): Promise<ArrayBuffer> {
  const { s3 } = await clientAndPrefix()
  const res = await s3.send(new GetObjectCommand({ Bucket: USER_DATA_BUCKET, Key: key }))
  const bytes = await res.Body!.transformToByteArray()
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
}

export async function deleteSession(key: string): Promise<void> {
  const { s3 } = await clientAndPrefix()
  await s3.send(new DeleteObjectCommand({ Bucket: USER_DATA_BUCKET, Key: key }))
}

/** Delete by file name, when the repo key is not known here. A file the repo does not have is
 *  not an error: S3 answers a delete of a missing object as done. */
export async function deleteSessionNamed(name: string): Promise<void> {
  const { s3, prefix } = await clientAndPrefix()
  await s3.send(new DeleteObjectCommand({ Bucket: USER_DATA_BUCKET, Key: `${prefix}${name}` }))
}

// ---------- Per-file notes ----------
// Stored as one small JSON document per user at private/<id>/_notes.json: { "<file name>": "<note>" }.
const NOTES_FILE = '_notes.json'

/** Remove every object under the signed-in user's prefix: sessions, notes and workspace state. */
export async function deleteAllUserData(): Promise<number> {
  const { s3, prefix } = await clientAndPrefix()
  let deleted = 0
  let token: string | undefined
  do {
    const res = await s3.send(new ListObjectsV2Command({ Bucket: USER_DATA_BUCKET, Prefix: prefix, ContinuationToken: token }))
    for (const o of res.Contents ?? []) {
      if (!o.Key) continue
      await s3.send(new DeleteObjectCommand({ Bucket: USER_DATA_BUCKET, Key: o.Key }))
      deleted++
    }
    token = res.IsTruncated ? res.NextContinuationToken : undefined
  } while (token)
  return deleted
}

export type Notes = Record<string, string>

export async function loadNotes(): Promise<Notes> {
  const { s3, prefix } = await clientAndPrefix()
  try {
    const res = await s3.send(new GetObjectCommand({ Bucket: USER_DATA_BUCKET, Key: `${prefix}${NOTES_FILE}` }))
    const text = await res.Body!.transformToString()
    const parsed: unknown = JSON.parse(text)
    return parsed && typeof parsed === 'object' ? (parsed as Notes) : {}
  } catch (e) {
    if ((e as { name?: string }).name === 'NoSuchKey') return {}
    throw e
  }
}

export async function saveNotes(notes: Notes): Promise<void> {
  const { s3, prefix } = await clientAndPrefix()
  const clean: Notes = {}
  for (const [k, v] of Object.entries(notes)) if (v.trim()) clean[k] = v.trim()
  await s3.send(
    new PutObjectCommand({
      Bucket: USER_DATA_BUCKET,
      Key: `${prefix}${NOTES_FILE}`,
      Body: JSON.stringify(clean),
      ContentType: 'application/json',
    }),
  )
}

// ---------- Per-file info shown under each repo entry ----------
// Filled from the logger's catalog when a session comes from the Solo 2, and from the parsed
// file when it is opened or uploaded, so the list can show date, laps, best lap and track
// without downloading anything.
const META_FILE = '_meta.json'

export interface SessionInfo {
  /** ISO start time, when known */
  date?: string
  laps?: number
  /** Best complete lap, milliseconds */
  bestMs?: number
  track?: string
  /** The logger it was downloaded from (a known-device id), when it came from one */
  device?: string
}
export type SessionInfos = Record<string, SessionInfo>

export async function loadMeta(): Promise<SessionInfos> {
  const { s3, prefix } = await clientAndPrefix()
  try {
    const res = await s3.send(new GetObjectCommand({ Bucket: USER_DATA_BUCKET, Key: `${prefix}${META_FILE}` }))
    const parsed: unknown = JSON.parse(await res.Body!.transformToString())
    return parsed && typeof parsed === 'object' ? (parsed as SessionInfos) : {}
  } catch (e) {
    if ((e as { name?: string }).name === 'NoSuchKey') return {}
    throw e
  }
}

export async function saveMeta(meta: SessionInfos): Promise<void> {
  const { s3, prefix } = await clientAndPrefix()
  await s3.send(
    new PutObjectCommand({ Bucket: USER_DATA_BUCKET, Key: `${prefix}${META_FILE}`, Body: JSON.stringify(meta), ContentType: 'application/json' }),
  )
}

/** Merge new entries into the repo's info file (read-modify-write; last writer wins per name). */
export async function mergeMeta(entries: SessionInfos): Promise<SessionInfos> {
  const current = await loadMeta()
  const next = { ...current, ...entries }
  await saveMeta(next)
  return next
}

/** One line of info for a file, e.g. "16/10/2026 14:03 · 9 laps · best 1:32.899 · THill WST". */
export function describeInfo(i: SessionInfo | undefined): string {
  if (!i) return ''
  const parts: string[] = []
  if (i.date) {
    const d = new Date(i.date)
    if (!Number.isNaN(d.getTime())) parts.push(formatDateTime(d))
  }
  if (i.laps != null) parts.push(`${i.laps} laps`)
  if (i.bestMs) {
    const s = i.bestMs / 1000
    const m = Math.floor(s / 60)
    parts.push(`best ${m}:${(s - m * 60).toFixed(3).padStart(6, '0')}`)
  }
  if (i.track) parts.push(i.track)
  return parts.join(' · ')
}

// ---------- Workspace state in the repo ----------
// Lets a signed-in user pick up where they left off on another device. Only repo sessions
// are recorded, since local files cannot be reopened elsewhere.
const STATE_FILE = '_state.json'

export interface CloudState {
  sessions: { id: string; name: string; cloudKey: string }[]
  view: {
    selected: string[]
    reference?: string
    channelNames: string[]
    showDelta: boolean
    showSummary?: boolean
    showAccel?: boolean
    accelMode?: AccelMode
    showTraction?: boolean
  }
}

export async function loadCloudState(): Promise<CloudState | null> {
  const { s3, prefix } = await clientAndPrefix()
  try {
    const res = await s3.send(new GetObjectCommand({ Bucket: USER_DATA_BUCKET, Key: `${prefix}${STATE_FILE}` }))
    const parsed = JSON.parse(await res.Body!.transformToString()) as Partial<CloudState>
    if (!parsed || !Array.isArray(parsed.sessions) || !parsed.view) return null
    return { sessions: parsed.sessions, view: parsed.view }
  } catch (e) {
    if ((e as { name?: string }).name === 'NoSuchKey') return null
    throw e
  }
}

export async function saveCloudState(state: CloudState): Promise<void> {
  const { s3, prefix } = await clientAndPrefix()
  await s3.send(
    new PutObjectCommand({
      Bucket: USER_DATA_BUCKET,
      Key: `${prefix}${STATE_FILE}`,
      Body: JSON.stringify(state),
      ContentType: 'application/json',
    }),
  )
}
